import Badges from '@jetshop/ui/ProductList/Badges';
import React, { Fragment, useEffect, useState } from 'react';
import { styled } from 'linaria/react';
import Image from '@jetshop/ui/Image';
import Thumbs from './Thumbs';
import { SliderStyles, sliderSettings } from '../../ui/SlickSlider';
import Slider from 'react-slick';
import { BadgeWrapper } from '../../CategoryPage/ProductCard';
import { Above } from '@jetshop/ui/Breakpoints';
import { theme } from '../../Theming/Theming';
import ImageGallery from './ImageGallery';
import { PercentPrice } from '../Price/PercentPrice';
import PlayIcon from '../../ui/icons/PlayIcon';
import { YoutubeSlide, getYoutubeVideo } from './YoutubeVideo';

const Wrapper = styled('div')`
  position: relative;
  //overflow: hidden;
  display: flex;
  justify-content: flex-end;
  flex-direction: column;
  ${theme.above.lg} {
    flex-direction: row;
  }
  ${theme.below.sm} {
    .thumbs-wrapper {
      display: none;
    }
  }
`;

const MainSlider = styled('div')`
  position: relative;
  width: 100%;
  order: 1;
  ${theme.above.lg} {
    width: 80%;
    order: 2;
  }
  ${theme.above.md} {
    border: 1px solid ${theme.colors.borderLight};
  }
  .slick-slider {
    margin: 0 0 !important;
    .slick-slide {
      padding: 0 0 !important;
    }
  }
`;

const ImageWrapper = styled('div')`
  cursor: pointer;
  display: block !important;
  &:focus {
    outline: none;
  }
`;

const VideoWrapper = styled('div')`
  position: relative;
  padding-bottom: 100%;
  video {
    position: absolute;
    width: 100%;
    height: auto;
    top: 50%;
    transform: translateY(-50%);
  }
`;

//JUMPS TO THE YOUTUBE SLIDE ON MOBILE, WHERE NO THUMBS ARE SHOWN
const VideoButton = styled('button')`
  position: absolute;
  right: 0.75rem;
  bottom: 0.75rem;
  display: flex;
  align-items: center;
  padding: 0.4rem 0.8rem;
  border: 0;
  border-radius: 1rem;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  font-size: 0.85rem;
  cursor: pointer;
  svg {
    margin-right: 0.4rem;
  }
`;

const Video = ({ data }) => {
  return (
    <VideoWrapper>
      <video controls>
        <source src={data.url} type="video/mp4" />
        Your browser does not support the video tag.
      </video>
    </VideoWrapper>
  );
};

const ImageSlider = ({
  product,
  className = '',
  selectedVariation,
  customData
}) => {
  //BADGES
  const badges = product.badges;

  //CHECK FOR VIDEO DATA
  const videoData =
    customData && customData.filter(item => item.key === 'product-video')[0];
  const videos = (videoData && videoData.listValues) || [];

  //YOUTUBE VIDEO IS ADDED AS THE LAST SLIDE
  const youtubeId = getYoutubeVideo(customData);

  //IMAGES
  const only_images = product.images;

  //SLIDER ITEMS
  const images = product
    ? [
        ...only_images,
        ...videos.map(item => {
          return { video: true, url: item };
        }),
        ...(youtubeId
          ? [
              {
                youtube: youtubeId,
                url: `https://www.youtube.com/watch?v=${youtubeId}`
              }
            ]
          : [])
      ]
    : [];
  const youtubeIndex = youtubeId ? images.length - 1 : -1;

  //SLIDER REF
  let imageSlider;

  //CLICKED IMAGE
  const [activeImage, setActiveImage] = useState(false);

  //ACTIVE SLIDER IMAGE
  const [activeSliderImage, setActiveSliderImage] = useState(0);

  const priceObject = selectedVariation ? selectedVariation : product;

  //UPDATE FOR VARIATION IMAGE
  useEffect(() => {
    if (selectedVariation?.images?.length > 0) {
      for (let i = 0; i < images.length; i++) {
        if (
          images[i].url === selectedVariation.images[0].url &&
          activeSliderImage !== i
        )
          selectSliderImage(i, true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVariation]);

  const goTo = index => {
    imageSlider.slickGoTo(index);
  };
  const selectSliderImage = (i, fromThumb) => {
    if (fromThumb) goTo(i);
    setActiveSliderImage(i);
  };
  return (
    <Wrapper className={className}>
      <MainSlider className="main-wrapper main-slider">
        <SliderStyles>
          <Slider
            {...{
              ...sliderSettings,
              speed: 200,
              beforeChange: (current, next) => selectSliderImage(next)
            }}
            ref={slider => (imageSlider = slider)}
          >
            {images.map((image, index) => (
              <ImageWrapper
                key={index}
                onClick={() => {
                  //A SWIPE ENDS WITH A CLICK AFTER THE SLIDE HAS CHANGED, IGNORE IT
                  if (index !== activeSliderImage) return;
                  if (!image.video && !image.youtube) setActiveImage(image);
                }}
              >
                {image.youtube ? (
                  <YoutubeSlide
                    id={image.youtube}
                    active={index === activeSliderImage}
                  />
                ) : image.video ? (
                  <Video data={image} />
                ) : (
                  <Image
                    critical={index === 0 ? true : false}
                    src={image.url}
                    sizes={[1, 1, 1, 700]}
                    aspect={theme.productPage.imageAspect}
                    crop={false}
                  />
                )}
              </ImageWrapper>
            ))}
          </Slider>
        </SliderStyles>
        <BadgeWrapper>
          <Badges badges={badges} />
        </BadgeWrapper>
        <PercentPrice
          className="badge"
          price={priceObject.price}
          previousPrice={priceObject.previousPrice}
        />
        {youtubeIndex !== -1 && activeSliderImage !== youtubeIndex && (
          <Above breakpoint="sm">
            {matches =>
              !matches && (
                <VideoButton
                  onClick={() => selectSliderImage(youtubeIndex, true)}
                >
                  <PlayIcon />
                  Video
                </VideoButton>
              )
            }
          </Above>
        )}
      </MainSlider>
      <Above breakpoint="sm">
        {matches => (
          <Fragment>
            {matches ? (
              <Thumbs
                images={images}
                selectSliderImage={selectSliderImage}
                activeSliderImage={activeSliderImage}
              />
            ) : null}
          </Fragment>
        )}
      </Above>
      {activeImage && (
        <ImageGallery
          images={only_images}
          setActiveImage={setActiveImage}
          activeSliderImage={activeSliderImage}
        />
      )}
    </Wrapper>
  );
};

export default ImageSlider;
